import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useI18n } from "@/contexts/I18nContext";
import { DASHBOARD_URL } from "@/lib/supabase";
import NewsletterForm from "@/components/shared/NewsletterForm";
import { useObservableError } from "@/hooks/useObservableError";

const LOGIN_HREF = DASHBOARD_URL ? `${DASHBOARD_URL}/` : "/login";

const ENV_FOOTER_ADDRESS = import.meta.env.VITE_FOOTER_ADDRESS as string | undefined;
const ENV_FOOTER_PHONE_DISPLAY = import.meta.env.VITE_FOOTER_PHONE_DISPLAY as string | undefined;
const ENV_FOOTER_PHONE_HREF = import.meta.env.VITE_FOOTER_PHONE_HREF as string | undefined;
const ENV_FOOTER_OSM_BBOX = import.meta.env.VITE_FOOTER_OSM_BBOX as string | undefined;

export default function SharedFooter() {
  const { t } = useI18n();
  const captureError = useObservableError();

  function revokeChatConsent() {
    try {
      // Bug fix: previously left the key at "revoked" → readConsent() !== null →
      // banner never re-opened, so users couldn't grant again. Pure removal allows re-prompt.
      localStorage.removeItem("swissez_cookie_consent");
      if (Array.isArray(window.$crisp)) {
        window.$crisp.push(["do", "session:reset"]);
        window.$crisp.push(["do", "chat:hide"]);
      }
      document.cookie.split(";").forEach((c) => {
        const name = (c.split("=")[0] ?? "").trim();
        if (name.startsWith("crisp-") || name.startsWith("_cfuvid")) {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        }
      });
      window.dispatchEvent(new Event("swissez-consent-changed"));
      toast.success(t("site.footer.consent_revoked"));
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      captureError(e, { route: "shared-footer", stage: "revoke-chat-consent" });
    }
  }

  return (
    <footer className="relative border-t border-border/30 pt-16 pb-8 px-6 md:px-8">
      <div className="absolute inset-0 bg-grid-fine opacity-20 pointer-events-none" />
      <div className="max-w-[1100px] mx-auto relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-12">
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <picture>
                <source srcSet="/logo-knot-lg.avif" type="image/avif" />
                <source srcSet="/logo-knot-lg.webp" type="image/webp" />
                <img src="/logo-knot-lg.png" alt="Swissez" width={32} height={32} className="h-8 w-auto" />
              </picture>
              <span className="text-lg font-bold tracking-tight">swiss<span className="text-primary">ez</span></span>
            </Link>
            <p className="text-xs text-muted-foreground leading-relaxed max-w-[260px]">
              {t("site.footer.tagline_lead")} <span className="italic text-primary">{t("site.footer.tagline_highlight")}</span>. {t("site.footer.tagline")}
            </p>
          </div>
          <div>
            <h5 className="text-xs font-bold uppercase tracking-wider mb-4 text-foreground/80">{t("site.footer.col_product")}</h5>
            <ul className="space-y-2.5">
              <li><Link to="/prezzi" className="text-xs text-muted-foreground hover:text-primary transition-colors duration-300">{t("site.footer.link_prezzi")}</Link></li>
              <li><Link to="/simulatore" className="text-xs text-muted-foreground hover:text-primary transition-colors duration-300">{t("site.footer.link_simulatore")}</Link></li>
              <li><Link to="/faq" className="text-xs text-muted-foreground hover:text-primary transition-colors duration-300">{t("site.footer.link_faq")}</Link></li>
              <li><a href={LOGIN_HREF} rel="noopener" className="text-xs text-muted-foreground hover:text-primary transition-colors duration-300">{t("site.footer.link_login")}</a></li>
              <li><Link to="/registrazione" className="text-xs text-muted-foreground hover:text-primary transition-colors duration-300">{t("site.footer.link_register")}</Link></li>
            </ul>
          </div>
          <div>
            <h5 className="text-xs font-bold uppercase tracking-wider mb-4 text-foreground/80">{t("site.footer.col_resources")}</h5>
            <ul className="space-y-2.5">
              <li><Link to="/blog" className="text-xs text-muted-foreground hover:text-primary transition-colors duration-300">{t("site.footer.link_blog")}</Link></li>
              <li><Link to="/blog/aprire-ditta-individuale-ticino" className="text-xs text-muted-foreground hover:text-primary transition-colors duration-300">{t("site.footer.link_ditta")}</Link></li>
              <li><Link to="/blog/deduzioni-fiscali-ticino-2026" className="text-xs text-muted-foreground hover:text-primary transition-colors duration-300">{t("site.footer.link_deduzioni")}</Link></li>
              <li><Link to="/blog/quanto-costa-fiduciario-ticino" className="text-xs text-muted-foreground hover:text-primary transition-colors duration-300">{t("site.footer.link_cost")}</Link></li>
              <li><Link to="/vs-fiduciario-tradizionale" className="text-xs text-muted-foreground hover:text-primary transition-colors duration-300">{t("site.footer.link_vs_trad")}</Link></li>
            </ul>
          </div>
          <div>
            <h5 className="text-xs font-bold uppercase tracking-wider mb-4 text-foreground/80">{t("site.footer.col_company")}</h5>
            <ul className="space-y-2.5">
              <li><Link to="/fiduciari" className="text-xs text-muted-foreground hover:text-primary transition-colors duration-300">{t("site.footer.link_fiduciari")}</Link></li>
              <li><Link to="/frontalieri" className="text-xs text-muted-foreground hover:text-primary transition-colors duration-300">{t("site.footer.link_frontalieri")}</Link></li>
              <li><Link to="/contatto" className="text-xs text-muted-foreground hover:text-primary transition-colors duration-300">{t("site.footer.link_contact")}</Link></li>
              <li><Link to="/privacy" className="text-xs text-muted-foreground hover:text-primary transition-colors duration-300">{t("site.footer.link_privacy")}</Link></li>
              <li><Link to="/termini" className="text-xs text-muted-foreground hover:text-primary transition-colors duration-300">{t("site.footer.link_terms")}</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-border/30 pt-8 mb-8">
          <div className="max-w-md mx-auto text-center">
            <h5 className="text-sm font-bold mb-2">{t("site.newsletter.heading")}</h5>
            <p className="text-xs text-muted-foreground mb-4">
              {t("site.newsletter.subheading")}
            </p>
            <NewsletterForm source="footer" />
          </div>
        </div>

        {/* Dove ci troviamo: indirizzo + mappa OpenStreetMap (no Google Maps per privacy nLPD/GDPR).
            NOTE FOUNDER: streetAddress e telefono sono PLACEHOLDER. Sostituire con valori reali
            quando la sede legale è registrata e il numero di telefono aziendale è attivo. */}
        <div className="border-t border-border/30 pt-8 mb-8">
          <div className="max-w-[1100px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
            <div>
              <h5 className="text-sm font-bold uppercase tracking-wider mb-3 text-foreground/80">
                {t("site.footer.location_heading")}
              </h5>
              <address className="not-italic text-xs text-muted-foreground leading-relaxed space-y-1">
                <p className="font-semibold text-foreground/90">Swissez Sàrl</p>
                <p>{ENV_FOOTER_ADDRESS || t("site.footer.contact.address")}</p>
                <p>{t("site.footer.contact.address2")}</p>
                <p className="pt-2">
                  <a href={`tel:${ENV_FOOTER_PHONE_HREF || t("site.footer.contact.phone_href")}`} className="hover:text-primary transition-colors duration-300">
                    {ENV_FOOTER_PHONE_DISPLAY || t("site.footer.contact.phone_display")}
                  </a>
                </p>
                <p>
                  <a href="mailto:info@swissez.ch" className="hover:text-primary transition-colors duration-300">
                    info@swissez.ch
                  </a>
                </p>
              </address>
            </div>
            <div className="rounded-2xl overflow-hidden border border-border/40">
              <iframe
                title="Mappa sede Swissez — Lugano"
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${ENV_FOOTER_OSM_BBOX || "8.94,46.00,8.96,46.02"}&layer=mapnik`}
                width="100%"
                height="200"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-[200px] border-0"
              />
            </div>
          </div>
        </div>
        <div className="border-t border-border/30 pt-6 text-center text-xs text-muted-foreground/50">
          {t("site.footer.copyright", { year: String(new Date().getFullYear()) })} |{" "}
          <Link to="/privacy" className="hover:text-primary transition-colors duration-300">{t("site.footer.link_privacy")}</Link> |{" "}
          <Link to="/termini" className="hover:text-primary transition-colors duration-300">{t("site.footer.link_terms")}</Link> |{" "}
          <button type="button" onClick={revokeChatConsent} className="hover:text-primary transition-colors duration-300 underline-offset-2 hover:underline">
            {t("site.footer.revoke_chat")}
          </button>
        </div>
      </div>
    </footer>
  );
}
